import { useEffect, useMemo, useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import {
  StickyNote, Plus, Pencil, Trash2, Pin, PinOff, Loader2, Save, X, Search,
  CheckCircle2, Archive, RotateCcw, Link2, Copy,
} from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

const COLOR_PALETTE = [
  { v: "yellow", bg: "bg-yellow-100", border: "border-yellow-300", chip: "bg-yellow-400" },
  { v: "green", bg: "bg-emerald-100", border: "border-emerald-300", chip: "bg-emerald-500" },
  { v: "blue", bg: "bg-sky-100", border: "border-sky-300", chip: "bg-sky-500" },
  { v: "pink", bg: "bg-pink-100", border: "border-pink-300", chip: "bg-pink-500" },
  { v: "purple", bg: "bg-violet-100", border: "border-violet-300", chip: "bg-violet-500" },
  { v: "orange", bg: "bg-orange-100", border: "border-orange-300", chip: "bg-orange-500" },
];
const colorStyle = (c) => COLOR_PALETTE.find((x) => x.v === c) || COLOR_PALETTE[0];

const EMPTY = { title: "", body: "", color: "yellow", pinned: false, linked_type: null, linked_id: null, linked_label: "" };

export default function Notes() {
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dlg, setDlg] = useState(null); // {mode: "create"|"edit", form}
  const [busy, setBusy] = useState(false);
  const [search, setSearch] = useState("");
  const [tab, setTab] = useState("active"); // "active" | "archived"
  const [completeTarget, setCompleteTarget] = useState(null); // nota da completare

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/notes?status=${tab}`);
      setNotes(data || []);
    }
    catch (e) { toast.error("Errore caricamento appunti"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [tab]);

  const openCreate = () => setDlg({ mode: "create", form: { ...EMPTY } });
  const openEdit = (n) => setDlg({ mode: "edit", form: {
    ...EMPTY, ...n,
    linked_label: n.linked_label || "",
  } });

  const save = async () => {
    const f = dlg.form;
    if (!f.body || !f.body.trim()) { toast.error("Scrivi il contenuto dell'appunto"); return; }
    const payload = {
      title: f.title, body: f.body, color: f.color, pinned: f.pinned,
      linked_type: f.linked_type || null,
      linked_id: f.linked_id || null,
      linked_label: f.linked_label || null,
    };
    setBusy(true);
    try {
      if (dlg.mode === "create") {
        await api.post("/notes", payload);
        toast.success("Appunto salvato");
      } else {
        await api.put(`/notes/${f.id}`, payload);
        toast.success("Appunto aggiornato");
      }
      setDlg(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
    finally { setBusy(false); }
  };

  const togglePin = async (n) => {
    try {
      await api.post(`/notes/${n.id}/toggle-pin`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
  };

  const remove = async (n) => {
    if (!window.confirm(`Eliminare l'appunto${n.title ? ` "${n.title}"` : ""}?`)) return;
    try {
      await api.delete(`/notes/${n.id}`);
      toast.success("Appunto eliminato");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
  };

  const doComplete = async (action) => {
    // action: "archive" | "delete"
    if (!completeTarget) return;
    setBusy(true);
    try {
      await api.post(`/notes/${completeTarget.id}/complete?action=${action}`);
      toast.success(action === "delete" ? "Appunto completato ed eliminato" : "Appunto completato e archiviato");
      setCompleteTarget(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
    finally { setBusy(false); }
  };

  const restoreNote = async (n) => {
    try {
      await api.post(`/notes/${n.id}/restore`);
      toast.success("Appunto riportato tra gli attivi"); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
  };

  const filtered = useMemo(() => {
    const s = search.trim().toLowerCase();
    if (!s) return notes;
    return notes.filter((n) =>
      (n.title || "").toLowerCase().includes(s) ||
      (n.body || "").toLowerCase().includes(s)
    );
  }, [notes, search]);

  return (
    <div className="space-y-4" data-testid="notes-page">
      <header className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <p className="text-xs uppercase tracking-wider text-slate-500">Personale</p>
          <h1 className="font-display text-2xl sm:text-3xl font-semibold tracking-tight flex items-center gap-2">
            <StickyNote className="h-6 w-6 text-emerald-600" /> Appunti rapidi
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            I tuoi appunti personali — visibili solo a te. Salvati istantaneamente.
          </p>
        </div>
        <Button
          data-testid="notes-add"
          onClick={openCreate}
          className="bg-emerald-600 hover:bg-emerald-700 text-white"
        >
          <Plus className="h-4 w-4 mr-1.5" /> Nuovo appunto
        </Button>
      </header>

      {/* Tabs Attivi / Archivio */}
      <div className="flex items-center gap-2 border-b border-slate-200">
        <button
          type="button"
          data-testid="notes-tab-active"
          onClick={() => setTab("active")}
          className={`px-3 py-2 text-sm font-medium border-b-2 transition ${tab === "active" ? "border-emerald-600 text-emerald-700" : "border-transparent text-slate-500 hover:text-slate-700"}`}
        >
          <StickyNote className="h-4 w-4 inline mr-1" /> Attivi
        </button>
        <button
          type="button"
          data-testid="notes-tab-archived"
          onClick={() => setTab("archived")}
          className={`px-3 py-2 text-sm font-medium border-b-2 transition ${tab === "archived" ? "border-slate-600 text-slate-700" : "border-transparent text-slate-500 hover:text-slate-700"}`}
        >
          <Archive className="h-4 w-4 inline mr-1" /> Archivio
        </button>
      </div>

      <div className="relative max-w-md">
        <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
        <Input
          data-testid="notes-search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Cerca per titolo o contenuto…"
          className="pl-8 h-9 text-sm"
        />
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-16 text-slate-400">
          <Loader2 className="h-5 w-5 animate-spin mr-2" /> Caricamento…
        </div>
      ) : filtered.length === 0 ? (
        <Card className="border-dashed border-2 border-slate-200">
          <CardContent className="text-center py-16">
            <StickyNote className="h-12 w-12 text-slate-300 mx-auto mb-3" />
            <p className="text-slate-500 text-sm">
              {notes.length === 0 ? "Nessun appunto. Inizia subito a scriverne uno!" : "Nessun risultato per la ricerca."}
            </p>
            {notes.length === 0 && (
              <Button
                data-testid="notes-add-empty"
                onClick={openCreate}
                className="mt-4 bg-emerald-600 hover:bg-emerald-700 text-white"
              >
                <Plus className="h-4 w-4 mr-1.5" /> Crea il primo appunto
              </Button>
            )}
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
          {filtered.map((n) => {
            const c = colorStyle(n.color);
            return (
              <div
                key={n.id}
                data-testid={`note-${n.id}`}
                onDoubleClick={() => openEdit(n)}
                title="Doppio click per aprire"
                className={`relative rounded-lg border ${c.bg} ${c.border} p-3 shadow-sm hover:shadow-md transition group cursor-pointer select-none`}
              >
                {n.pinned && (
                  <div className="absolute top-1.5 right-1.5 text-amber-600" title="Fissato">
                    <Pin className="h-3.5 w-3.5 fill-current" />
                  </div>
                )}
                {n.title && <div className="font-semibold text-sm text-slate-900 mb-1.5 pr-5">{n.title}</div>}
                <pre className="whitespace-pre-wrap text-sm text-slate-800 font-sans leading-relaxed break-words max-h-48 overflow-y-auto">
                  {n.body}
                </pre>
                {n.linked_label && (
                  <div className="mt-2 inline-flex items-center gap-1 text-[10px] bg-white/60 border border-slate-300/50 rounded px-1.5 py-0.5 text-slate-700">
                    <Link2 className="h-2.5 w-2.5" />
                    <span className="font-medium">{n.linked_type === "client" ? "Cliente" : n.linked_type === "policy" ? "Polizza" : n.linked_type === "quote" ? "Preventivo" : "Collegato"}:</span>
                    <span className="truncate max-w-[140px]">{n.linked_label}</span>
                  </div>
                )}
                <div className="mt-3 pt-2 border-t border-slate-300/40 flex items-center justify-between text-[10px] text-slate-600" onDoubleClick={(e) => e.stopPropagation()}>
                  <span>{new Date(n.updated_at).toLocaleString("it-IT", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}</span>
                  <div className="flex items-center gap-0.5 opacity-60 group-hover:opacity-100 transition">
                    {tab === "active" ? (
                      <>
                        <button
                          type="button"
                          data-testid={`note-copy-${n.id}`}
                          onClick={async () => {
                            const text = (n.title ? `${n.title}\n\n` : "") + (n.body || "");
                            try {
                              if (navigator.clipboard && window.isSecureContext) {
                                await navigator.clipboard.writeText(text);
                              } else {
                                const ta = document.createElement("textarea");
                                ta.value = text;
                                ta.style.position = "fixed";
                                ta.style.opacity = "0";
                                document.body.appendChild(ta);
                                ta.select();
                                document.execCommand("copy");
                                document.body.removeChild(ta);
                              }
                              toast.success("Appunto copiato negli appunti");
                            } catch {
                              toast.error("Impossibile copiare l'appunto");
                            }
                          }}
                          className="p-1 hover:bg-white/50 rounded text-slate-700"
                          title="Copia contenuto"
                        >
                          <Copy className="h-3 w-3" />
                        </button>
                        <button
                          type="button"
                          data-testid={`note-complete-${n.id}`}
                          onClick={() => setCompleteTarget(n)}
                          className="p-1 hover:bg-white/50 rounded text-emerald-700"
                          title="Memo completato"
                        >
                          <CheckCircle2 className="h-3.5 w-3.5" />
                        </button>
                        <button
                          type="button"
                          data-testid={`note-pin-${n.id}`}
                          onClick={() => togglePin(n)}
                          className="p-1 hover:bg-white/50 rounded"
                          title={n.pinned ? "Sblocca" : "Fissa in alto"}
                        >
                          {n.pinned ? <PinOff className="h-3 w-3" /> : <Pin className="h-3 w-3" />}
                        </button>
                        <button
                          type="button"
                          data-testid={`note-edit-${n.id}`}
                          onClick={() => openEdit(n)}
                          className="p-1 hover:bg-white/50 rounded"
                          title="Modifica"
                        >
                          <Pencil className="h-3 w-3" />
                        </button>
                        <button
                          type="button"
                          data-testid={`note-del-${n.id}`}
                          onClick={() => remove(n)}
                          className="p-1 hover:bg-white/50 rounded text-rose-700"
                          title="Elimina"
                        >
                          <Trash2 className="h-3 w-3" />
                        </button>
                      </>
                    ) : (
                      <>
                        <button
                          type="button"
                          data-testid={`note-restore-${n.id}`}
                          onClick={() => restoreNote(n)}
                          className="p-1 hover:bg-white/50 rounded text-emerald-700"
                          title="Ripristina"
                        >
                          <RotateCcw className="h-3 w-3" />
                        </button>
                        <button
                          type="button"
                          data-testid={`note-del-${n.id}`}
                          onClick={() => remove(n)}
                          className="p-1 hover:bg-white/50 rounded text-rose-700"
                          title="Elimina definitivamente"
                        >
                          <Trash2 className="h-3 w-3" />
                        </button>
                      </>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Dialog crea/modifica */}
      <Dialog open={!!dlg} onOpenChange={(o) => !o && setDlg(null)}>
        <DialogContent className="max-w-md" data-testid="note-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <StickyNote className="h-5 w-5 text-emerald-600" />
              {dlg?.mode === "create" ? "Nuovo appunto" : "Modifica appunto"}
            </DialogTitle>
          </DialogHeader>
          {dlg && (
            <div className="space-y-3">
              <div>
                <label className="text-xs font-medium block mb-1">Titolo (facoltativo)</label>
                <Input
                  data-testid="note-title"
                  value={dlg.form.title}
                  onChange={(e) => setDlg({ ...dlg, form: { ...dlg.form, title: e.target.value } })}
                  placeholder="Es. Chiamare Mario"
                />
              </div>
              <div>
                <label className="text-xs font-medium block mb-1">Contenuto <span className="text-red-500">*</span></label>
                <Textarea
                  data-testid="note-body"
                  rows={6}
                  value={dlg.form.body}
                  onChange={(e) => setDlg({ ...dlg, form: { ...dlg.form, body: e.target.value } })}
                  placeholder="Scrivi qui il tuo appunto…"
                  autoFocus
                />
              </div>
              <div>
                <label className="text-xs font-medium block mb-1.5">Colore</label>
                <div className="flex flex-wrap gap-2">
                  {COLOR_PALETTE.map((c) => (
                    <button
                      key={c.v}
                      type="button"
                      data-testid={`note-color-${c.v}`}
                      onClick={() => setDlg({ ...dlg, form: { ...dlg.form, color: c.v } })}
                      className={`w-7 h-7 rounded-full ${c.chip} ring-2 transition ${dlg.form.color === c.v ? "ring-slate-900 ring-offset-2" : "ring-transparent"}`}
                      title={c.v}
                    />
                  ))}
                </div>
              </div>
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <input
                  type="checkbox"
                  data-testid="note-pinned"
                  checked={!!dlg.form.pinned}
                  onChange={(e) => setDlg({ ...dlg, form: { ...dlg.form, pinned: e.target.checked } })}
                  className="h-4 w-4"
                />
                <Pin className="h-3.5 w-3.5 text-amber-600" />
                <span>Fissa in alto</span>
              </label>
              <div className="border-t border-slate-200 pt-2">
                <label className="text-xs font-medium block mb-1 flex items-center gap-1">
                  <Link2 className="h-3 w-3 text-slate-500" /> Collegato a (facoltativo)
                </label>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                  <select
                    data-testid="note-linked-type"
                    className="h-9 rounded-md border border-slate-300 px-2 text-xs bg-white"
                    value={dlg.form.linked_type || ""}
                    onChange={(e) => setDlg({ ...dlg, form: { ...dlg.form, linked_type: e.target.value || null } })}
                  >
                    <option value="">Nessuno</option>
                    <option value="client">Cliente</option>
                    <option value="policy">Polizza</option>
                    <option value="quote">Preventivo</option>
                  </select>
                  <Input
                    data-testid="note-linked-label"
                    className="col-span-2 h-9 text-xs"
                    placeholder="Es. Mario Rossi · POL-2026-001"
                    value={dlg.form.linked_label || ""}
                    onChange={(e) => setDlg({ ...dlg, form: { ...dlg.form, linked_label: e.target.value } })}
                    disabled={!dlg.form.linked_type}
                  />
                </div>
                <p className="text-[10px] text-slate-400 mt-0.5">Etichetta libera per ricordare a quale cliente/polizza/preventivo si riferisce.</p>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setDlg(null)}>
              <X className="h-4 w-4 mr-1" /> Annulla
            </Button>
            <Button
              data-testid="note-save"
              onClick={save}
              disabled={busy}
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
            >
              {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />} Salva
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog memo completato — archivia o elimina */}
      <Dialog open={!!completeTarget} onOpenChange={(o) => !o && setCompleteTarget(null)}>
        <DialogContent className="max-w-sm" data-testid="note-complete-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CheckCircle2 className="h-5 w-5 text-emerald-600" /> Memo completato
            </DialogTitle>
          </DialogHeader>
          <p className="text-sm text-slate-600">
            Cosa vuoi fare con l&apos;appunto{completeTarget?.title ? <strong> &quot;{completeTarget.title}&quot;</strong> : null}?
          </p>
          <div className="space-y-2 mt-2">
            <button
              type="button"
              data-testid="note-complete-archive"
              onClick={() => doComplete("archive")}
              disabled={busy}
              className="w-full text-left p-3 rounded-md border border-slate-200 hover:border-emerald-400 hover:bg-emerald-50 transition flex items-start gap-3"
            >
              <Archive className="h-5 w-5 text-emerald-600 mt-0.5 flex-shrink-0" />
              <div>
                <div className="font-semibold text-sm">Sposta in Archivio</div>
                <div className="text-xs text-slate-500">Lo nascondi dagli attivi, ma puoi sempre ritrovarlo nell&apos;archivio dedicato e ripristinarlo se serve.</div>
              </div>
            </button>
            <button
              type="button"
              data-testid="note-complete-delete"
              onClick={() => doComplete("delete")}
              disabled={busy}
              className="w-full text-left p-3 rounded-md border border-slate-200 hover:border-rose-400 hover:bg-rose-50 transition flex items-start gap-3"
            >
              <Trash2 className="h-5 w-5 text-rose-600 mt-0.5 flex-shrink-0" />
              <div>
                <div className="font-semibold text-sm">Elimina definitivamente</div>
                <div className="text-xs text-slate-500">Cancella subito senza tenerne traccia. Azione irreversibile.</div>
              </div>
            </button>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCompleteTarget(null)}>Annulla</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
